import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { ChevronRight, ClipboardList } from 'lucide-react'
import { Button } from '../ui/button'
import { DashboardSection } from './DashboardSection'
import { ProgressBar } from './ProgressBar'
import type { DashboardTrainingPlan } from '../../types/dashboard'

interface TrainingPlanSectionProps {
  trainingPlan: DashboardTrainingPlan
}

export function TrainingPlanSection({ trainingPlan }: TrainingPlanSectionProps) {
  const { t } = useTranslation()
  const { active, has_plans: hasPlans } = trainingPlan

  if (!active) {
    return (
      <DashboardSection title={t('dashboard.trainingPlan.title')}>
        {hasPlans ? (
          // A draft, or one still waiting on a coach: point at it rather than
          // offering to create yet another plan.
          <Button asChild variant="outline" className="w-full">
            <Link to="/training-plans">{t('dashboard.trainingPlan.viewPlans')}</Link>
          </Button>
        ) : (
          <Button asChild size="lg" className="h-14 w-full text-sm">
            <Link to="/training-plans/new">
              <ClipboardList className="mr-2 size-4" />
              {t('dashboard.trainingPlan.create')}
            </Link>
          </Button>
        )}
      </DashboardSection>
    )
  }

  const title =
    active.goal?.trim() ||
    t(`trainingPlans.discipline.${active.discipline ?? 'bouldering'}`)

  return (
    <DashboardSection title={t('dashboard.trainingPlan.title')}>
      <Link
        to={`/training-plans/${active.id}`}
        className="flex flex-col gap-2 rounded-xl border p-3 transition-colors hover:border-primary"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium">{title}</span>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        </div>

        <p className="text-xs text-muted-foreground">
          {active.start_date && (
            <span>
              {t('dashboard.trainingPlan.startsOn')}{' '}
              <span className="font-medium text-foreground">
                {format(parseISO(active.start_date), 'dd/MM')}
              </span>
            </span>
          )}
          {active.start_date && active.length_weeks != null && <span> · </span>}
          {active.length_weeks != null && (
            <span>{t('dashboard.trainingPlan.weeks', { count: active.length_weeks })}</span>
          )}
        </p>

        <ProgressBar value={active.weeks_elapsed} total={active.length_weeks ?? 0} />
      </Link>
    </DashboardSection>
  )
}
